import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * FILTROS RÁPIDOS DE HISTÓRICO — fonte única do ERP.
 *
 * REGRA: o padrão é "todos". Um default "hoje" faria registros antigos
 * parecerem apagados, que é exatamente o defeito auditado.
 */
export const PERIOD_OPTIONS = [
  { value: "todos", label: "Todo o histórico" },
  { value: "hoje", label: "Hoje" },
  { value: "ontem", label: "Ontem" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
  { value: "mes", label: "Este mês" },
  { value: "mes_anterior", label: "Mês anterior" },
  { value: "ano", label: "Este ano" },
  { value: "personalizado", label: "Personalizado" },
];

export const MONTHS = [
  ["1", "Janeiro"], ["2", "Fevereiro"], ["3", "Março"], ["4", "Abril"],
  ["5", "Maio"], ["6", "Junho"], ["7", "Julho"], ["8", "Agosto"],
  ["9", "Setembro"], ["10", "Outubro"], ["11", "Novembro"], ["12", "Dezembro"],
];

const CURRENT_YEAR = new Date().getFullYear();

export const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - i);

export const DEFAULT_HISTORY_FILTERS = {
  period: "todos",
  year: String(CURRENT_YEAR),
  month: String(new Date().getMonth() + 1),
  startDate: "",
  endDate: "",
  search: "",
  hasOccurrence: "todos",
  technicianId: "",
  includeDeleted: "ativos",
};

/** Motivos de exclusão (espelham GET /history/delete-reasons). */
export const DELETE_REASON_OPTIONS = [
  { value: "registro_de_teste", label: "Registro de teste" },
  { value: "lancamento_incorreto", label: "Lançamento incorreto" },
  { value: "duplicidade", label: "Duplicidade" },
  { value: "outro", label: "Outro" },
];

export function getDefaultFilters() {
  return { ...DEFAULT_HISTORY_FILTERS, page: 1, pageSize: 50 };
}

/**
 * Monta os query params da API a partir do estado de filtros.
 * `extra` carrega cliente/unidade/estrutura/tipo de sala.
 */
export function buildHistoryParams(filters, extra = {}) {
  const params = {
    page: filters.page || 1,
    page_size: filters.pageSize || 50,
    ...extra,
  };

  if (filters.period === "personalizado") {
    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;
  } else if (filters.period === "mes") {
    params.period = "mes";
    params.year = Number(filters.year);
    params.month = Number(filters.month);
  } else if (filters.period === "ano") {
    params.period = "ano";
    params.year = Number(filters.year);
  } else if (filters.period) {
    params.period = filters.period;
  }

  if (filters.search) params.search = filters.search;
  if (filters.hasOccurrence === "sim") params.has_occurrence = true;
  if (filters.hasOccurrence === "nao") params.has_occurrence = false;
  if (filters.technicianId) params.technician_id = filters.technicianId;

  return params;
}

export function periodLabel(filters) {
  if (filters.period === "mes") {
    const month = MONTHS.find(([v]) => v === filters.month)?.[1] || "";
    return `${month}/${filters.year}`;
  }

  if (filters.period === "ano") return `ano ${filters.year}`;

  if (filters.period === "personalizado") {
    return `${filters.startDate || "início"} a ${filters.endDate || "hoje"}`;
  }

  return PERIOD_OPTIONS.find((o) => o.value === filters.period)?.label || "";
}

/** Paginação server-side com total de registros e de páginas. */
export function HistoryPagination({ meta, onPage, testId = "history" }) {
  if (!meta) return null;

  const { page = 1, pages = 0, total = 0, page_size: pageSize = 50 } = meta;

  return (
    <div
      className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-border"
      data-testid={`${testId}-pagination`}
    >
      <p className="text-xs text-muted-foreground" data-testid={`${testId}-total`}>
        {total} registro{total === 1 ? "" : "s"} · {pages} página{pages === 1 ? "" : "s"} · {pageSize} por página
      </p>

      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft size={14} />
        </Button>

        <span className="text-xs px-2" data-testid={`${testId}-page`}>
          Página {page} de {Math.max(pages, 1)}
        </span>

        <Button
          variant="outline"
          size="sm"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          data-testid={`${testId}-next`}
        >
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}